import {
  EmptyState,
  Header,
  Icon,
  IconButton,
  ModalDialog,
  PrimaryButton,
  Space,
  Text,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { CreatePOPDFLabelSetFormPresenter } from '@webapp/modules/purchasing/features/create-po-pdf-label-set/presenters/CreatePOPDFLabelSetFormPresenter'
import { POPDFLabelSetFieldsPresenter } from '@webapp/modules/purchasing/features/get-po-pdf-label-set-fields/presenters/POPDFLabelSetFieldsPresenter'
import { RenamePOPDFLabelSetFormPresenter } from '@webapp/modules/purchasing/features/rename-po-pdf-label-set/presenters/RenamePOPDFLabelSetFormPresenter'
import { PDFLabelSetSelect } from '@webapp/modules/vendor/components/PDFLabelSetSelect' // eslint-disable-line import/no-internal-modules
import messages from './messages'
import { usePOPDFLabelsView } from './usePOPDFLabelsView'

/**
 * Feature Set: PO PDF labels section (PO PDF Labels tab)
 *
 * Orchestrates:
 * - Heading/description row + "PDF label" create button (FS composition)
 * - The selector row: existing PDFLabelSetSelect (ADR-3) + trash/star/pencil
 *   icon buttons wired to the hook-only command features
 * - The sectioned override editor when a set is active, else the empty state
 * - The FS-owned create/rename ModalDialogs wrapping the shared name form
 *   presenters (delete's danger ConfirmationModal is owned by its hook)
 */
export const POPDFLabelsView = () => {
  const intl = useProcurifyIntl()

  const {
    data: { activeSet, currentPrimaryKey },
    states: { isLoading, modalState },
    methods: {
      openCreateModal,
      openRenameModal,
      closeModal,
      handleSelectChange,
      handleCreateSuccess,
      handleRenameSuccess,
      onOverrideCommit,
      onDeleteSet,
      onToggleDefault,
    },
  } = usePOPDFLabelsView()

  const isActiveSetDefault =
    activeSet !== null && activeSet.key === currentPrimaryKey

  return (
    <>
      <Space>
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space size='small'>
            <Header as='h3'>{intl.formatMessage(messages.heading)}</Header>
            <Text>{intl.formatMessage(messages.description)}</Text>
          </Space>
          <PrimaryButton icon='add' onClick={openCreateModal}>
            {intl.formatMessage(messages.createButton)}
          </PrimaryButton>
        </Space>

        <Space direction='horizontal' justify='space-between' align='center'>
          <Space direction='horizontal' align='center'>
            <Space.Item width='fit-content'>
              <Header as='h4'>
                {intl.formatMessage(messages.selectorLabel)}
              </Header>
            </Space.Item>
            <Space.Item width='300px'>
              {/* Remount on active-set changes so the AsyncSelect's cached
                  options reload after create/rename/delete (BHV-037/038/041 —
                  the self-contained select caches its options for its
                  lifetime and cannot be invalidated externally, ADR-3) */}
              <PDFLabelSetSelect
                key={activeSet?.key ?? 'no-active-set'}
                aria-label={intl.formatMessage(messages.selectorAriaLabel)}
                value={activeSet}
                onChange={handleSelectChange}
              />
            </Space.Item>
          </Space>
          <Space direction='horizontal' size='small'>
            <IconButton
              icon='delete'
              aria-label={intl.formatMessage(messages.deleteLabelsButton)}
              htmlTitle={intl.formatMessage(messages.deleteLabelsButton)}
              disabled={!activeSet}
              onClick={onDeleteSet}
            />
            <IconButton
              aria-label={intl.formatMessage(messages.setAsDefaultButton)}
              htmlTitle={intl.formatMessage(messages.setAsDefaultButton)}
              disabled={!activeSet}
              iconComponent={
                <Icon
                  icon='star-solid'
                  color={isActiveSetDefault ? 'warning' : 'lightGrey'}
                />
              }
              onClick={onToggleDefault}
            />
            <IconButton
              icon='edit'
              aria-label={intl.formatMessage(messages.editLabelsButton)}
              htmlTitle={intl.formatMessage(messages.editLabelsButton)}
              disabled={!activeSet}
              onClick={openRenameModal}
            />
          </Space>
        </Space>

        {activeSet ? (
          <POPDFLabelSetFieldsPresenter
            labelSet={activeSet}
            onOverrideCommit={onOverrideCommit}
          />
        ) : (
          !isLoading && (
            <EmptyState
              header={intl.formatMessage(messages.emptyStateHeader)}
              content={intl.formatMessage(messages.emptyStateContent)}
            />
          )
        )}
      </Space>

      {/* Create/rename modal shells — owned by the Feature Set per ADR-9 */}
      <ModalDialog
        title={intl.formatMessage(messages.createModalTitle)}
        maxWidth='md'
        open={modalState === 'create'}
        onClose={closeModal}
      >
        {modalState === 'create' && (
          <CreatePOPDFLabelSetFormPresenter
            onSuccess={handleCreateSuccess}
            onCancel={closeModal}
          />
        )}
      </ModalDialog>

      <ModalDialog
        title={intl.formatMessage(messages.renameModalTitle)}
        maxWidth='md'
        open={modalState === 'rename'}
        onClose={closeModal}
      >
        {modalState === 'rename' && activeSet && (
          <RenamePOPDFLabelSetFormPresenter
            labelSet={activeSet}
            onSuccess={handleRenameSuccess}
            onCancel={closeModal}
          />
        )}
      </ModalDialog>
    </>
  )
}

POPDFLabelsView.displayName = 'POPDFLabelsView'
